import { useState, useEffect, useRef } from "react";
import "./css/adminproductdetail.css";
import { getProductDetail } from "../../../utils/adminProductService";
import { getCategoryList } from "../../../utils/commonService";
import { apiPutFormData } from "../../../utils/apiClient";

// ENUM 코드 -> 한글 상태 매핑
const ENUM_STATE_MAP = {
  PENDING: "대기",
  ON_SALE: "판매 중",
  ENDED: "판매 종료",
  SOLDOUT: "품절",
  REJECTED: "판매 제재",
};

// 한글 -> ENUM 코드 역방향 매핑
const STATE_ENUM_MAP = {
  대기: "PENDING",
  "판매 중": "ON_SALE",
  "판매 종료": "ENDED",
  품절: "SOLDOUT",
  "판매 제재": "REJECTED",
};

const AdminProductDetail = ({ productCode, onClose, onUpdate }) => {
  const [productData, setProductData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [categoryList, setCategoryList] = useState([]);
  const [descImgFiles, setDescImgFiles] = useState([]);
  const [allImages, setAllImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const descEditorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const descImgInputRef = useRef(null);
  const imgInputRef = useRef(null);

  const fetchProductDetail = async () => {
    const result = await getProductDetail(productCode);
    if (result.success) {
      setProductData(result.data);
    } else {
      alert(result.error);
      onClose();
    }
  };

  const fetchCategories = async () => {
    const result = await getCategoryList();
    if (result.success) {
      setCategoryList(result.data);
    }
  };

  useEffect(() => {
    if (productCode) {
      fetchProductDetail();
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCode]);

  // productData 로드 시 editForm 설정
  useEffect(() => {
    if (productData) {
      const foundCategory = categoryList.find(
        (cat) => cat.categoryNm === productData.categoryNm,
      );
      setEditForm({
        productNm: productData.productNm || "",
        categoryIdx: foundCategory?.categoryIdx || null,
        categoryNm: foundCategory?.categoryNm || productData.categoryNm || "",
        isAdult: productData.isAdult,
        productPrice: productData.productPrice || 0,
        productAmount: productData.productAmount || 0,
        productState:
          STATE_ENUM_MAP[productData.productState] || productData.productState,
        isStock: productData.isStock,
        isNego: productData.isNego,
        isSale: productData.isSale,
        restrictReason: productData.restrictReason || "",
      });
    }
    // categoryList는 의존성에서 제외 - categoryList 로드 시 editForm 전체 재초기화 방지
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productData]);

  // categoryList 로드 완료 후 categoryIdx만 보정
  useEffect(() => {
    if (categoryList.length > 0 && editForm.categoryNm) {
      const foundCategory = categoryList.find(
        (cat) => cat.categoryNm === editForm.categoryNm,
      );
      if (foundCategory && editForm.categoryIdx !== foundCategory.categoryIdx) {
        setEditForm((prev) => ({
          ...prev,
          categoryIdx: foundCategory.categoryIdx,
          categoryNm: foundCategory.categoryNm,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryList]);

  // productData 로드 시 allImages 초기화
  useEffect(() => {
    if (productData) {
      if (productData.imgs?.files?.length > 0) {
        setAllImages(
          productData.imgs.files.map((img) => ({
            type: "existing",
            preview: img.fileUrl,
            data: img,
            name: img.originalName || img.fileOriginNm || "",
          })),
        );
      } else {
        setAllImages([]);
      }
      setSelectedImageIndex(0);
    }
  }, [productData]);

  // 수정 모드 진입 시 contentEditable에 기존 내용 세팅
  useEffect(() => {
    if (isEditMode && descEditorRef.current && productData) {
      descEditorRef.current.innerHTML = productData.productDescription || "";
      setDescImgFiles([]);
    }
  }, [isEditMode, productData]);

  const getMimeTypeFromFileName = (fileName) => {
    if (!fileName) return "image/jpeg";
    const ext = fileName.split(".").pop()?.toLowerCase();
    return (
      {
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        gif: "image/gif",
        webp: "image/webp",
        bmp: "image/bmp",
      }[ext] || "image/jpeg"
    );
  };

  const convertExistingImageToFile = async (imageUrl, fileName) => {
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const finalName = fileName || imageUrl.split("/").pop() || "image.jpg";
      return new File([blob], finalName, {
        type: getMimeTypeFromFileName(finalName),
      });
    } catch {
      return null;
    }
  };

  const handleProductImageSelect = (file) => {
    if (allImages.length >= 3) {
      alert("이미지는 최대 3개까지만 첨부 가능합니다.");
      return;
    }
    if (!file.type.startsWith("image/")) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("이미지 파일 크기는 10MB를 초과할 수 없습니다.");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setAllImages((prev) => [
        ...prev,
        { type: "new", data: file, preview: reader.result, name: file.name },
      ]);
    };
    reader.readAsDataURL(file);
  };

  const handleProductImageChange = (e) => {
    if (e.target.files?.[0]) handleProductImageSelect(e.target.files[0]);
    e.target.value = "";
  };

  const removeImageFromList = (index) => {
    setAllImages((prev) => {
      const next = prev.filter((_, i) => i !== index);
      setSelectedImageIndex((cur) =>
        next.length === 0 ? 0 : cur >= next.length ? next.length - 1 : cur,
      );
      return next;
    });
  };

  const handleDescImageInsert = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (file.size > 10 * 1024 * 1024) {
      alert("이미지 파일 크기는 10MB를 초과할 수 없습니다.");
      return;
    }

    const blobUrl = URL.createObjectURL(file);
    const img = document.createElement("img");
    img.src = blobUrl;
    img.style.maxWidth = "100%";
    img.style.height = "auto";
    img.style.display = "block";

    const editor = descEditorRef.current;
    editor.focus();

    // 저장된 커서 위치가 있으면 거기에, 없으면 에디터 끝에 삽입
    const sel = window.getSelection();
    let range;
    if (
      savedRangeRef.current &&
      editor.contains(savedRangeRef.current.commonAncestorContainer)
    ) {
      range = savedRangeRef.current;
    } else {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    savedRangeRef.current = null;

    sel.removeAllRanges();
    sel.addRange(range);
    range.deleteContents();
    range.insertNode(img);
    range.setStartAfter(img);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);

    setDescImgFiles((prev) => [...prev, { blobUrl, file }]);
  };

  const handleSave = async () => {
    if (allImages.length === 0) {
      alert("메인 이미지를 1장 이상 등록해주세요.");
      return;
    }

    const formDataObj = new FormData();

    // 기본 필드
    Object.entries(editForm).forEach(([key, val]) => {
      if (val !== null && val !== undefined) formDataObj.append(key, val);
    });
    formDataObj.set("isAdult", false);

    // 상품 설명 (blob URL → 플레이스홀더 치환)
    let descHtml = descEditorRef.current?.innerHTML || "";
    if (descImgFiles.length > 0) {
      descImgFiles.forEach(({ blobUrl }, idx) => {
        descHtml = descHtml.split(blobUrl).join(`__PENDING_IMG_${idx}__`);
      });
      descImgFiles.forEach(({ file }) =>
        formDataObj.append("productDescriptionImgs", file),
      );
    }
    formDataObj.set("productDescription", descHtml);

    // 상품 이미지 (기존 → fetch 변환, 신규 → 그대로)
    for (const img of allImages) {
      if (img.type === "new") {
        formDataObj.append("productImgs", img.data);
      } else {
        const file = await convertExistingImageToFile(img.preview, img.name);
        if (file) formDataObj.append("productImgs", file);
      }
    }

    try {
      const response = await apiPutFormData(
        `/admin/product/${productCode}`,
        formDataObj,
      );
      if (response.ok) {
        alert("저장되었습니다.");
        setIsEditMode(false);
        setDescImgFiles([]);
        fetchProductDetail();
        if (onUpdate) onUpdate();
      } else {
        alert("상품 정보 수정에 실패했습니다.");
      }
    } catch {
      alert("네트워크 오류가 발생했습니다.");
    }
  };

  const handleCancel = () => {
    setEditForm({
      productNm: productData.productNm || "",
      categoryIdx:
        categoryList.find((cat) => cat.categoryNm === productData.categoryNm)
          ?.categoryIdx || null,
      categoryNm: productData.categoryNm || "",
      isAdult: productData.isAdult,
      productPrice: productData.productPrice || 0,
      productAmount: productData.productAmount || 0,
      productState:
        STATE_ENUM_MAP[productData.productState] || productData.productState,
      isStock: productData.isStock,
      isNego: productData.isNego,
      isSale: productData.isSale,
      restrictReason: productData.restrictReason || "",
    });
    if (descEditorRef.current) {
      descEditorRef.current.innerHTML = productData.productDescription || "";
    }
    if (productData.imgs?.files?.length > 0) {
      setAllImages(
        productData.imgs.files.map((img) => ({
          type: "existing",
          preview: img.fileUrl,
          data: img,
          name: img.originalName || img.fileOriginNm || "",
        })),
      );
    } else {
      setAllImages([]);
    }
    setSelectedImageIndex(0);
    setDescImgFiles([]);
    setIsEditMode(false);
  };

  if (!productData) return null;

  return (
    <div className="adminproductdetail-backdrop" onClick={onClose}>
      <div
        className="adminproductdetail-img"
        onClick={(e) => e.stopPropagation()}
      >
        {isEditMode && (
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={imgInputRef}
            onChange={handleProductImageChange}
          />
        )}
        <div className="adminproductdetail-main-image-wrapper">
          {allImages.length > 0 ? (
            <img
              src={allImages[selectedImageIndex]?.preview}
              alt={productData.productNm}
              className="adminproductdetail-main-image"
            />
          ) : isEditMode ? (
            <div
              className="adminproductdetail-image-add-area"
              onClick={(e) => {
                e.stopPropagation();
                imgInputRef.current?.click();
              }}
            >
              <span>+ 이미지 추가</span>
            </div>
          ) : (
            <div className="adminproductdetail-no-image">이미지 없음</div>
          )}
        </div>
        <div className="adminproductdetail-thumbnail-container">
          <button
            type="button"
            className="adminproductdetail-arrow-btn"
            onClick={() =>
              setSelectedImageIndex((prev) =>
                prev > 0 ? prev - 1 : allImages.length - 1,
              )
            }
            disabled={allImages.length <= 1}
          >
            ◀
          </button>
          <div className="adminproductdetail-thumbnail-list">
            {[0, 1, 2].map((slotIndex) => (
              <div
                key={slotIndex}
                className={`adminproductdetail-thumbnail-slot ${
                  allImages[slotIndex] && selectedImageIndex === slotIndex
                    ? "active"
                    : ""
                } ${allImages[slotIndex] ? "has-image" : "empty"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (allImages[slotIndex]) setSelectedImageIndex(slotIndex);
                  else if (isEditMode && allImages.length < 3)
                    imgInputRef.current?.click();
                }}
              >
                {allImages[slotIndex] ? (
                  <>
                    <img
                      src={allImages[slotIndex].preview}
                      alt={`썸네일 ${slotIndex + 1}`}
                      className="adminproductdetail-thumbnail-image"
                    />
                    {isEditMode && (
                      <button
                        type="button"
                        className="adminproductdetail-img-remove-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImageFromList(slotIndex);
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </>
                ) : isEditMode ? (
                  <span className="adminproductdetail-img-add-placeholder">
                    +
                  </span>
                ) : null}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="adminproductdetail-arrow-btn"
            onClick={() =>
              setSelectedImageIndex((prev) =>
                prev < allImages.length - 1 ? prev + 1 : 0,
              )
            }
            disabled={allImages.length <= 1}
          >
            ▶
          </button>
        </div>
      </div>
      <div
        className="adminproductdetail-wrap"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="adminproductdetail-header">
          <h2 className="adminproductdetail-title">상품 상세 정보</h2>
          <button className="adminproductdetail-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="adminproductdetail-content">
          {/* 상품 정보 섹션 */}
          <section className="adminproductdetail-section">
            <div className="adminproductdetail-section-header">
              <h3 className="adminproductdetail-section-title">상품 정보</h3>
              {isEditMode ? (
                <div className="adminproductdetail-section-actions">
                  <button
                    className="adminproductdetail-btn adminproductdetail-save-btn"
                    onClick={handleSave}
                  >
                    저장
                  </button>
                  <button
                    className="adminproductdetail-btn adminproductdetail-cancel-btn"
                    onClick={handleCancel}
                  >
                    취소
                  </button>
                </div>
              ) : (
                <button
                  className="adminproductdetail-btn adminproductdetail-edit-btn"
                  onClick={() => setIsEditMode(true)}
                >
                  수정
                </button>
              )}
            </div>
            <div className="adminproductdetail-grid">
              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">상품명</label>
                {isEditMode ? (
                  <input
                    type="text"
                    value={editForm.productNm}
                    onChange={(e) =>
                      setEditForm({ ...editForm, productNm: e.target.value })
                    }
                    className="adminproductdetail-input"
                  />
                ) : (
                  <span className="adminproductdetail-value">
                    {productData.productNm}
                  </span>
                )}
              </div>

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">상품 코드</label>
                <span className="adminproductdetail-value">
                  {productData.productCode}
                </span>
              </div>

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">카테고리</label>
                {isEditMode ? (
                  <select
                    value={String(editForm.categoryIdx || "")}
                    onChange={(e) => {
                      const selectedCategory = categoryList.find(
                        (cat) => cat.categoryIdx === parseInt(e.target.value),
                      );
                      setEditForm({
                        ...editForm,
                        categoryIdx: parseInt(e.target.value),
                        categoryNm: selectedCategory?.categoryNm || "",
                      });
                    }}
                    className="adminproductdetail-select"
                  >
                    <option value="">카테고리 선택</option>
                    {categoryList.map((category) => (
                      <option
                        key={category.categoryIdx}
                        value={String(category.categoryIdx)}
                      >
                        {category.categoryNm}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="adminproductdetail-value">
                    {productData.categoryNm}
                  </span>
                )}
              </div>

              {/* <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">성인상품</label>
                {isEditMode ? (
                  <select
                    value={String(editForm.isAdult)}
                    onChange={(e) => setEditForm({ ...editForm, isAdult: e.target.value === 'true' })}
                    className="adminproductdetail-select"
                  >
                    <option value="false">일반</option>
                    <option value="true">성인</option>
                  </select>
                ) : (
                  <span className="adminproductdetail-value">
                    {productData.isAdult ? "성인" : "일반"}
                  </span>
                )}
              </div> */}

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">상품 구분</label>
                {isEditMode ? (
                  <select
                    value={String(editForm.isStock)}
                    onChange={(e) =>
                      setEditForm({ ...editForm, isStock: e.target.value })
                    }
                    className="adminproductdetail-select"
                  >
                    <option value="false">일반</option>
                    <option value="true">서비스</option>
                  </select>
                ) : (
                  <span className="adminproductdetail-value">
                    {productData.isStock ? "서비스 상품" : "일반 상품"}
                  </span>
                )}
              </div>

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">가격</label>
                {isEditMode ? (
                  <input
                    type="number"
                    value={editForm.productPrice}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        productPrice: parseInt(e.target.value),
                      })
                    }
                    className="adminproductdetail-input"
                  />
                ) : (
                  <span className="adminproductdetail-value">
                    {productData.productPrice?.toLocaleString()}원
                  </span>
                )}
              </div>

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">재고</label>
                {isEditMode ? (
                  <input
                    type="number"
                    value={editForm.productAmount}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        productAmount: parseInt(e.target.value),
                      })
                    }
                    className="adminproductdetail-input"
                  />
                ) : (
                  <span className="adminproductdetail-value">
                    {productData.productAmount}
                  </span>
                )}
              </div>

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">판매상태</label>
                {isEditMode ? (
                  <select
                    value={String(editForm.productState)}
                    onChange={(e) => {
                      const newState = e.target.value;
                      const updates = { productState: newState };
                      if (["PENDING", "ENDED", "REJECTED"].includes(newState)) {
                        updates.isSale = false;
                        updates.isNego = false;
                      } else if (newState === "ON_SALE") {
                        updates.isSale = productData.isSale;
                        updates.isNego = productData.isNego;
                      }
                      setEditForm({ ...editForm, ...updates });
                    }}
                    className="adminproductdetail-select"
                  >
                    <option value="PENDING">판매 대기</option>
                    <option value="ON_SALE">판매 중</option>
                    <option value="ENDED">판매 종료</option>
                    <option value="SOLDOUT">품절</option>
                    <option value="REJECTED">이용 제한</option>
                  </select>
                ) : (
                  <span className="adminproductdetail-value">
                    {ENUM_STATE_MAP[productData.productState] ||
                      productData.productState}
                  </span>
                )}
              </div>

              {isEditMode && editForm.productState === "REJECTED" && (
                <div className="adminproductdetail-field adminproductdetail-field-full">
                  <label className="adminproductdetail-label">제재사유</label>
                  <textarea
                    value={editForm.restrictReason || ""}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        restrictReason: e.target.value,
                      })
                    }
                    className="adminproductdetail-textarea"
                    placeholder="제재 사유를 입력해주세요"
                    rows="3"
                  />
                </div>
              )}

              {!isEditMode && editForm.productState === "REJECTED" && (
                <div className="adminproductdetail-field adminproductdetail-field-full">
                  <label className="adminproductdetail-label">제재사유</label>
                  {productData.restrictReason ? (
                    <span className="adminproductdetail-value">
                      {productData.restrictReason}
                    </span>
                  ) : (
                    <span
                      className="adminproductdetail-value"
                      style={{ color: "#999" }}
                    >
                      제재사유 없음
                    </span>
                  )}
                </div>
              )}

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">할인</label>
                {isEditMode ? (
                  <select
                    value={String(editForm.isSale)}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        isSale: e.target.value === "true",
                      })
                    }
                    className="adminproductdetail-select"
                  >
                    <option value="false">미사용</option>
                    <option value="true">사용</option>
                  </select>
                ) : (
                  <span className="adminproductdetail-value">
                    {productData.isSale ? "사용" : "미사용"}
                  </span>
                )}
              </div>

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">네고</label>
                {isEditMode ? (
                  <select
                    value={String(editForm.isNego)}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        isNego: e.target.value === "true",
                      })
                    }
                    className="adminproductdetail-select"
                  >
                    <option value="false">미사용</option>
                    <option value="true">사용</option>
                  </select>
                ) : (
                  <span className="adminproductdetail-value">
                    {productData.isNego ? "사용" : "미사용"}
                  </span>
                )}
              </div>

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">등록일</label>
                <span className="adminproductdetail-value">
                  {new Date(productData.productRegDateTime).toLocaleString()}
                </span>
              </div>

              <div className="adminproductdetail-field adminproductdetail-field-full">
                <label className="adminproductdetail-label">
                  상품 상세설명
                </label>
                {isEditMode ? (
                  <div className="adminproductdetail-desc-editor-wrap">
                    <div className="adminproductdetail-desc-toolbar">
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        ref={descImgInputRef}
                        onChange={handleDescImageInsert}
                      />
                      <button
                        type="button"
                        className="adminproductdetail-btn adminproductdetail-desc-img-btn"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          // 커서 위치 저장 (onMouseDown은 포커스 이동 전에 발생)
                          const sel = window.getSelection();
                          if (sel && sel.rangeCount > 0) {
                            const r = sel.getRangeAt(0);
                            if (
                              descEditorRef.current?.contains(
                                r.commonAncestorContainer,
                              )
                            ) {
                              savedRangeRef.current = r.cloneRange();
                            }
                          }
                          descImgInputRef.current?.click();
                        }}
                      >
                        이미지 삽입
                      </button>
                    </div>
                    <div
                      ref={descEditorRef}
                      contentEditable
                      suppressContentEditableWarning
                      className="adminproductdetail-desc-editor"
                    />
                  </div>
                ) : (
                  <div
                    className="adminproductdetail-desc-view"
                    dangerouslySetInnerHTML={{
                      __html: productData.productDescription || "",
                    }}
                  />
                )}
              </div>
            </div>
          </section>

          {/* 가맹점 정보 섹션 */}
          <section className="adminproductdetail-section">
            <h3 className="adminproductdetail-section-header">가맹점 정보</h3>
            <div className="adminproductdetail-grid">
              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">가맹점명</label>
                <span className="adminproductdetail-value">
                  {productData.storeNm}
                </span>
              </div>

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">가맹점 코드</label>
                <span className="adminproductdetail-value">
                  {productData.storeCode}
                </span>
              </div>

              <div className="adminproductdetail-field adminproductdetail-field-full">
                <label className="adminproductdetail-label">가맹점 주소</label>
                <span className="adminproductdetail-value">
                  {productData.storeAddr} {productData.storeDetailAddr}
                </span>
              </div>

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">연락처</label>
                <span className="adminproductdetail-value">
                  {productData.storeTelNo}
                </span>
              </div>

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">영업일</label>
                <span className="adminproductdetail-value">
                  {productData.storeOpenDate?.join(", ") || "-"}
                </span>
              </div>

              <div className="adminproductdetail-field adminproductdetail-field-full">
                <label className="adminproductdetail-label">영업시간</label>
                <span className="adminproductdetail-value">
                  {productData.storeOpenTime} ~ {productData.storeCloseTime}
                </span>
              </div>
            </div>
          </section>

          {/* 상품 등록자 정보 섹션 */}
          <section className="adminproductdetail-section">
            <h3 className="adminproductdetail-section-header">상품 등록자</h3>
            <div className="adminproductdetail-grid">
              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">등록자 ID</label>
                <span className="adminproductdetail-value">
                  {productData.memberId}
                </span>
              </div>

              <div className="adminproductdetail-field">
                <label className="adminproductdetail-label">등록자명</label>
                <span className="adminproductdetail-value">
                  {productData.memberNm}
                </span>
              </div>
            </div>
          </section>
        </div>

        <div className="adminproductdetail-footer">
          <button
            className="adminproductdetail-btn adminproductdetail-close-footer-btn"
            onClick={onClose}
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminProductDetail;
